import { faRobot, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';

const EDIT_TYPES = {
  USER: 'user',
  AI: 'ai',
  NONE: 'none',
};

const EDIT_ICONS = {
  [EDIT_TYPES.USER]: {
    icon: faUser,
    color: '#007bff',
  },
  [EDIT_TYPES.AI]: {
    icon: faRobot,
    color: '#6f42c1',
    fallbackName: 'AI Assistant',
  },
};

const EditedByInfo = ({ report, usersByUri, config = {} }) => {
  const [showEmail, setShowEmail] = useState(false);

  const { auto_parsing_job: autoParsingJob, user } = report || {};

  // Determine edit type based on priority logic
  const getEditType = useCallback(() => {
    if (user) return EDIT_TYPES.USER;
    if (autoParsingJob && !user) return EDIT_TYPES.AI;
    return EDIT_TYPES.NONE;
  }, [user, autoParsingJob]);

  // Get edit information based on type
  const getEditInfo = useCallback(() => {
    const editType = getEditType();
    if (editType === EDIT_TYPES.NONE) return null;

    const iconConfig = EDIT_ICONS[editType];
    const mergedConfig = { ...iconConfig, ...config[editType] };

    if (editType === EDIT_TYPES.USER) {
      const userData = usersByUri?.[user];
      const email = userData?.username || userData?.emails?.[0] || null;
      const displayName = userData?.name || email;

      return {
        type: editType,
        icon: mergedConfig.icon,
        name: displayName,
        email,
        color: mergedConfig.color,
        hasInteraction: Boolean(email),
      };
    }

    if (editType === EDIT_TYPES.AI) {
      return {
        type: editType,
        icon: mergedConfig.icon,
        name: mergedConfig.fallbackName,
        email: null,
        color: mergedConfig.color,
        hasInteraction: false,
      };
    }

    return null;
  }, [getEditType, user, usersByUri, config]);

  // Handle name click for users with email
  const handleNameClick = useCallback(() => {
    setShowEmail(prev => !prev);
  }, []);

  const editInfo = getEditInfo();

  // Early return if no report data or edit information
  if (!report || !editInfo) return null;

  return (
    <div className='dp-edited-by-info' role='status' aria-label={`Last edited by ${editInfo.name}`}>
      <div className='dp-edited-by-content'>
        <FontAwesomeIcon
          fixedWidth
          icon={editInfo.icon}
          style={{
            color: editInfo.color,
            fontSize: '12px',
            width: '12px',
            height: '12px',
          }}
          aria-hidden='true'
        />
        <span className='dp-edited-by-text'>
          Edited by:
          <span
            className='dp-edited-by-name'
            style={{ cursor: editInfo.hasInteraction ? 'pointer' : 'default' }}
            tabIndex={editInfo.hasInteraction ? 0 : -1}
            role={editInfo.hasInteraction ? 'button' : 'text'}
            aria-expanded={editInfo.hasInteraction ? showEmail : undefined}
            onClick={editInfo.hasInteraction ? handleNameClick : undefined}
            onKeyDown={
              editInfo.hasInteraction
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNameClick();
                    }
                  }
                : undefined
            }
          >
            {editInfo.name}
          </span>
        </span>
      </div>
      {showEmail && editInfo.email && (
        <div className='dp-edited-by-email' title={editInfo.email}>
          {editInfo.email}
        </div>
      )}
    </div>
  );
};

EditedByInfo.propTypes = {
  report: PropTypes.shape({
    // eslint-disable-next-line camelcase
    auto_parsing_job: PropTypes.string,
    user: PropTypes.string,
  }),
  usersByUri: PropTypes.object,
  config: PropTypes.shape({
    [EDIT_TYPES.USER]: PropTypes.shape({
      icon: PropTypes.object,
      color: PropTypes.string,
      fallbackName: PropTypes.string,
    }),
    [EDIT_TYPES.AI]: PropTypes.shape({
      icon: PropTypes.object,
      color: PropTypes.string,
      fallbackName: PropTypes.string,
    }),
  }),
};

EditedByInfo.defaultProps = {
  report: null,
  usersByUri: {},
  config: {},
};

export default EditedByInfo;
